// Predios y Potreros. Ganadería → Predios y potreros.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPinned, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  Campo, Modal, Potrero, Predio, SortTh, Switch, fieldCls, ganRpc, num, useSort,
} from './shared';

const predioVacio = { id: '', nombre: '', codigo_sifae: '', ruc_ganadero: '', direccion: '', lat: '', lng: '', activo: true };
const potreroVacio = { id: '', predio_id: '', nombre: '', area_ha: '', activo: true };

export function PrediosPotreros() {
  const [tab, setTab] = useState<'predios' | 'potreros'>('predios');
  const tabBtn = (t: typeof tab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><MapPinned className="w-5 h-5" /> Predios y potreros</h1>
      <div className="flex gap-1 border-b pb-2">
        {tabBtn('predios', 'Predios')}
        {tabBtn('potreros', 'Potreros')}
      </div>
      {tab === 'predios' ? <PrediosTab /> : <PotrerosTab />}
    </div>
  );
}

function PrediosTab() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: predios = [], isFetching } = useQuery<Predio[]>({
    queryKey: ['gan-predios'], queryFn: () => ganRpc<Predio[]>('gan_predio_listar'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(predioVacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (p?: Predio) => {
    if (p) {
      setEditId(p.id);
      setForm({
        id: p.id, nombre: p.nombre, codigo_sifae: p.codigo_sifae ?? '', ruc_ganadero: p.ruc_ganadero ?? '',
        direccion: p.direccion ?? '', lat: p.lat != null ? String(p.lat) : '', lng: p.lng != null ? String(p.lng) : '', activo: p.activo,
      });
    } else { setEditId(null); setForm(predioVacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => ganRpc<Predio>('gan_predio_guardar', {
      _data: {
        id: editId || undefined,
        nombre: form.nombre, codigo_sifae: form.codigo_sifae || null, ruc_ganadero: form.ruc_ganadero || null,
        direccion: form.direccion || null, lat: form.lat === '' ? null : Number(form.lat), lng: form.lng === '' ? null : Number(form.lng),
        activo: form.activo,
      },
    }),
    onSuccess: () => { toast.success('Predio guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['gan-predios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => ganRpc('gan_predio_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Predio eliminado'); void qc.invalidateQueries({ queryKey: ['gan-predios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: Predio, key: string): string | number | null => {
    switch (key) {
      case 'nombre': return r.nombre;
      case 'sifae': return r.codigo_sifae;
      case 'ruc': return r.ruc_ganadero;
      case 'direccion': return r.direccion;
      default: return null;
    }
  }, []);
  const sort = useSort(predios, getVal);

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex justify-end">
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo predio</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Predios ({predios.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="sifae" sort={sort}>Código SIFAE</SortTh>
              <SortTh col="ruc" sort={sort}>RUC ganadero</SortTh>
              <SortTh col="direccion" sort={sort}>Dirección</SortTh>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin predios registrados</td></tr>}
              {sort.sorted.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.codigo_sifae ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.ruc_ganadero ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.direccion ?? '—'}</td>
                  <td className="py-2 pr-3">{p.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${p.nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar predio' : 'Nuevo predio'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código SIFAE"><input className={fieldCls} value={form.codigo_sifae} onChange={(e) => setForm((f) => ({ ...f, codigo_sifae: e.target.value }))} /></Campo>
              <Campo label="RUC del ganadero"><input className={fieldCls} value={form.ruc_ganadero} onChange={(e) => setForm((f) => ({ ...f, ruc_ganadero: e.target.value }))} /></Campo>
            </div>
            <Campo label="Dirección"><textarea className={`${fieldCls} h-16`} value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Latitud"><input type="number" step="0.0000001" className={fieldCls} placeholder="-0.1807" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} /></Campo>
              <Campo label="Longitud"><input type="number" step="0.0000001" className={fieldCls} placeholder="-78.4678" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} /></Campo>
            </div>
            {form.lat && form.lng && (
              <a className="text-xs text-primary hover:underline" target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps?q=${form.lat},${form.lng}`}>Ver ubicación en el mapa →</a>
            )}
            <Switch checked={form.activo} onChange={(v) => setForm((f) => ({ ...f, activo: v }))} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PotrerosTab() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: potreros = [], isFetching } = useQuery<Potrero[]>({
    queryKey: ['gan-potreros'], queryFn: () => ganRpc<Potrero[]>('gan_potrero_listar'),
  });
  const { data: predios = [] } = useQuery<Predio[]>({
    queryKey: ['gan-predios'], queryFn: () => ganRpc<Predio[]>('gan_predio_listar'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(potreroVacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (p?: Potrero) => {
    if (p) {
      setEditId(p.id);
      setForm({ id: p.id, predio_id: p.predio_id, nombre: p.nombre, area_ha: p.area_ha != null ? String(p.area_ha) : '', activo: p.activo });
    } else { setEditId(null); setForm({ ...potreroVacio, predio_id: predios[0]?.id ?? '' }); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => ganRpc<Potrero>('gan_potrero_guardar', {
      _data: {
        id: editId || undefined, predio_id: form.predio_id, nombre: form.nombre,
        area_ha: form.area_ha === '' ? null : Number(form.area_ha), activo: form.activo,
      },
    }),
    onSuccess: () => { toast.success('Potrero guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['gan-potreros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => ganRpc('gan_potrero_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Potrero eliminado'); void qc.invalidateQueries({ queryKey: ['gan-potreros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: Potrero, key: string): string | number | null => {
    switch (key) {
      case 'predio': return r.predio_nombre;
      case 'nombre': return r.nombre;
      case 'area': return r.area_ha != null ? Number(r.area_ha) : null;
      default: return null;
    }
  }, []);
  const sort = useSort(potreros, getVal);
  const totalHa = potreros.reduce((a, p) => a + (p.area_ha != null ? Number(p.area_ha) : 0), 0);

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex justify-end">
        <Button onClick={() => abrir()} disabled={!predios.length}><Plus className="w-4 h-4 mr-1" /> Nuevo potrero</Button>
      </div>
      {!predios.length && (
        <p className="text-sm text-muted-foreground">Crea primero un predio en la pestaña «Predios» para poder registrar sus potreros.</p>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Potreros ({potreros.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="predio" sort={sort}>Predio</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="area" sort={sort} align="right">Área (ha)</SortTh>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin potreros registrados</td></tr>}
              {sort.sorted.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.predio_nombre}</td>
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{p.area_ha != null ? num(p.area_ha, 2) : '—'}</td>
                  <td className="py-2 pr-3">{p.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${p.nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={3}>Totales ({sort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(totalHa, 2)}</td>
                  <td colSpan={2} />
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar potrero' : 'Nuevo potrero'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Predio *">
              <select className={fieldCls} value={form.predio_id} onChange={(e) => setForm((f) => ({ ...f, predio_id: e.target.value }))}>
                <option value="">— Selecciona —</option>
                {predios.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></Campo>
            <Campo label="Área (hectáreas)"><input type="number" min="0" step="0.01" className={fieldCls} value={form.area_ha} onChange={(e) => setForm((f) => ({ ...f, area_ha: e.target.value }))} /></Campo>
            <Switch checked={form.activo} onChange={(v) => setForm((f) => ({ ...f, activo: v }))} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.predio_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
